import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Grid, 
  Paper, 
  Tab, 
  Tabs, 
  Box, 
  Button, 
  Card, 
  CardContent, 
  LinearProgress,
  TextField, 
  Pagination, 
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel,
  Container,
  Avatar,
  Chip,
  alpha,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { getUserProfile, getUserCourses, getUserCertificates, getUserCourseHistory, getUserTestHistory } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import MyCalendar from '../componets/Calendar/Calendar';
import TestHistory from '../componets/TestHistory';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import SchoolIcon from '@mui/icons-material/School';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PendingIcon from '@mui/icons-material/Pending';

// Paleta profesional
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  info: '#74b9ff',
  purple: '#6c5ce7',
  orange: '#e17055',
  dark: '#2d3436'
};

const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [userCourses, setUserCourses] = useState([]);
  const [userCertificates, setUserCertificates] = useState([]);
  const [filteredCertificates, setFilteredCertificates] = useState([]);
  const [courseHistory, setCourseHistory] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [certificatePage, setCertificatePage] = useState(1);
  const [certificateSearchTerm, setCertificateSearchTerm] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [availableCourses, setAvailableCourses] = useState([]);
  const coursesPerPage = 6;
  const certificatesPerPage = 6;
  const [previewPdf, setPreviewPdf] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [deadlines, setDeadlines] = useState([]);
  const [testHistory, setTestHistory] = useState({ attempts: [] });

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      
      if (!token || !userId) {
        navigate('/login');
        return;
      }
  
      try {
        setLoading(true);
        const [
          profileResponse,
          coursesResponse,
          certificatesResponse,
          historyResponse,
          testHistoryResponse
        ] = await Promise.all([
          getUserProfile(),
          getUserCourses(userId),
          getUserCertificates(userId),
          getUserCourseHistory(userId),
          getUserTestHistory(userId)
        ]);
  
        setUserData(profileResponse.data);
        setUserCourses(coursesResponse);
        
        const sortedCertificates = (Array.isArray(certificatesResponse.data) ? certificatesResponse.data : []).sort((a, b) => 
          new Date(b.completion_date) - new Date(a.completion_date)
        );
        
        setUserCertificates(sortedCertificates);
        setFilteredCertificates(sortedCertificates);
        setCourseHistory(historyResponse.data);
        setTestHistory(testHistoryResponse);
  
        const uniqueCourses = [...new Set((Array.isArray(certificatesResponse.data) ? certificatesResponse.data : []).map(cert => cert.course_title))];
        setAvailableCourses(uniqueCourses);

        const courseDeadlines = coursesResponse
          .filter(course => course.deadline)
          .map(course => ({
            courseTitle: course.title,
            date: new Date(course.deadline),
            courseId: course.id
          }));
        setDeadlines(courseDeadlines);
        
      } catch (error) {
        console.error('Error fetching user data:', error);
        if (error.response?.status === 401) {
          logout();
          navigate('/login');
        }
        setError('Error al cargar los datos del usuario');
      } finally {
        setLoading(false);
      }
    };
  
    fetchUserData();
  }, [navigate, logout]);

  useEffect(() => {
    if (Array.isArray(userCertificates)) {
      const filtered = userCertificates.filter(cert => {
        const matchesSearch = cert.course_title.toLowerCase().includes(certificateSearchTerm.toLowerCase());
        const matchesCourse = !selectedCourse || cert.course_title === selectedCourse;
        return matchesSearch && matchesCourse;
      });
      
      setFilteredCertificates(filtered);
      setCertificatePage(1);
    }
  }, [certificateSearchTerm, selectedCourse, userCertificates]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCourseSelect = (event) => {
    setSelectedCourse(event.target.value);
  };

  const calculateAverageScore = (courses) => {
    const coursesWithScores = courses.filter(c => 
      c.test_score !== null && 
      c.test_score !== undefined && 
      !isNaN(c.test_score)
    );
  
    if (coursesWithScores.length === 0) return 0;
    const totalScore = coursesWithScores.reduce((sum, course) => sum + Number(course.test_score), 0);
    return (totalScore / coursesWithScores.length).toFixed(2);
  };

  const generateCalendarEvents = (history) => {
    return history.map(entry => ({
      title: `${entry.course_title} - Completado`,
      start: new Date(entry.completion_date),
      end: new Date(entry.completion_date),
      allDay: true,
    }));
  };

  const getProgressColor = (value) => {
    if (value < 40) return COLORS.error;
    if (value < 70) return COLORS.warning;
    return COLORS.success;
  };

  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = userCourses.slice(indexOfFirstCourse, indexOfLastCourse);

  const indexOfLastCertificate = certificatePage * certificatesPerPage;
  const indexOfFirstCertificate = indexOfLastCertificate - certificatesPerPage;
  const currentCertificates = filteredCertificates.slice(indexOfFirstCertificate, indexOfLastCertificate);

  const handleCourseClick = (courseId) => {
    navigate(`/course/${courseId}/view`);
  };

  const handleDownloadCertificate = (pdfPath, title) => {
    const url = `${process.env.REACT_APP_API_URL.replace('/api', '')}/${pdfPath}`;
    setPreviewPdf(url);
    setPreviewTitle(title || 'Certificado');
  };

  const handleClosePreview = () => {
    setPreviewPdf(null);
    setPreviewTitle('');
  };

  const handleActualDownload = () => {
    if (previewPdf) window.open(previewPdf, '_blank');
  };

  const getCourseStatus = (course) => {
    if (course.completed) return { text: 'Completado', color: COLORS.success, icon: <CheckCircleIcon /> };
    if (course.completed_lessons > 0) return { text: 'En progreso', color: COLORS.warning, icon: <AccessTimeIcon /> };
    return { text: 'No iniciado', color: COLORS.info, icon: <PendingIcon /> };
  };

  const tabs = [
    { label: 'Progreso', icon: <MenuBookIcon /> },
    { label: 'Calendario', icon: <CalendarMonthIcon /> },
    { label: 'Certificados', icon: <CardMembershipIcon /> },
    { label: 'Historial', icon: <HistoryIcon /> }
  ];

  // Loading state
  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center',
          background: 'linear-gradient(180deg, #f8fffe 0%, #f0f7ff 100%)'
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            boxShadow: `0 8px 32px ${alpha(COLORS.gradient1, 0.3)}`
          }}
        >
          <PersonIcon sx={{ fontSize: 40, color: 'white' }} />
        </Box>
        <Typography variant="h6" sx={{ color: COLORS.dark, fontWeight: 600, mb: 2 }}>
          Cargando tu perfil...
        </Typography>
        <Box sx={{ width: 200 }}>
          <LinearProgress 
            sx={{ 
              borderRadius: 2,
              height: 6,
              backgroundColor: alpha(COLORS.gradient1, 0.2),
              '& .MuiLinearProgress-bar': {
                background: `linear-gradient(90deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                borderRadius: 2
              }
            }} 
          />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography color="error" variant="h5">{error}</Typography>
      </Container>
    );
  }

  if (!userData) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5">No se encontraron datos del usuario.</Typography>
      </Container>
    );
  }

  const completedCoursesPercentage = userCourses.length > 0 
    ? (userCourses.filter(c => c.completed).length / userCourses.length) * 100 
    : 0;
  const averageScorePercentage = calculateAverageScore(userCourses);

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f8fffe 0%, #f0f7ff 100%)' }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            mb: 4,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: `0 20px 60px ${alpha(COLORS.gradient1, 0.3)}`
          }}
        >
          {/* Decorative circles */}
          <Box sx={{ position: 'absolute', top: -40, right: -40, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
          <Box sx={{ position: 'absolute', bottom: -20, left: '40%', width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'rgba(255,255,255,0.2)',
                fontSize: '2rem',
                fontWeight: 800,
                border: '4px solid rgba(255,255,255,0.3)'
              }}
            >
              {userData.nombre?.charAt(0).toUpperCase() || userData.username?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 900, letterSpacing: -0.5 }}>
                {userData.nombre} {userData.apellido}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip
                  icon={<BadgeIcon />}
                  label={`@${userData.username}`}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: 'white' }
                  }}
                />
                <Chip
                  icon={<EmailIcon />}
                  label={userData.email}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: 'white' }
                  }}
                />
                <Chip
                  label={userData.role === 'admin' ? '👑 Administrador' : '👤 Usuario'}
                  sx={{
                    bgcolor: userData.role === 'admin' ? COLORS.warning : 'rgba(255,255,255,0.2)',
                    color: userData.role === 'admin' ? COLORS.dark : 'white',
                    fontWeight: 700
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: `2px solid ${alpha(COLORS.primary, 0.15)}`,
                background: `linear-gradient(135deg, ${alpha(COLORS.primary, 0.05)} 0%, white 100%)`,
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 12px 24px ${alpha(COLORS.primary, 0.15)}` }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: alpha(COLORS.primary, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <SchoolIcon sx={{ color: COLORS.primary, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: COLORS.dark }}>{userCourses.length}</Typography>
                  <Typography variant="body2" sx={{ color: '#636e72', fontWeight: 500 }}>Cursos asignados</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: `2px solid ${alpha(COLORS.success, 0.15)}`,
                background: `linear-gradient(135deg, ${alpha(COLORS.success, 0.05)} 0%, white 100%)`,
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 12px 24px ${alpha(COLORS.success, 0.15)}` }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: alpha(COLORS.success, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckCircleIcon sx={{ color: COLORS.success, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: COLORS.dark }}>{userCourses.filter(c => c.completed).length}</Typography>
                  <Typography variant="body2" sx={{ color: '#636e72', fontWeight: 500 }}>Completados</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: `2px solid ${alpha(COLORS.purple, 0.15)}`,
                background: `linear-gradient(135deg, ${alpha(COLORS.purple, 0.05)} 0%, white 100%)`,
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 12px 24px ${alpha(COLORS.purple, 0.15)}` }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: alpha(COLORS.purple, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <EmojiEventsIcon sx={{ color: COLORS.purple, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: COLORS.dark }}>{userCertificates.length}</Typography>
                  <Typography variant="body2" sx={{ color: '#636e72', fontWeight: 500 }}>Certificados</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: `2px solid ${alpha(COLORS.orange, 0.15)}`,
                background: `linear-gradient(135deg, ${alpha(COLORS.orange, 0.05)} 0%, white 100%)`,
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 12px 24px ${alpha(COLORS.orange, 0.15)}` }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: alpha(COLORS.orange, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TrendingUpIcon sx={{ color: COLORS.orange, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: COLORS.dark }}>{averageScorePercentage}%</Typography>
                  <Typography variant="body2" sx={{ color: '#636e72', fontWeight: 500 }}>Nota media</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Progress Bars */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            border: '2px solid #e0e0e0'
          }}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontWeight: 600, color: COLORS.dark }}>Cursos completados</Typography>
                <Typography sx={{ fontWeight: 700, color: getProgressColor(completedCoursesPercentage) }}>
                  {userCourses.filter(c => c.completed).length} / {userCourses.length}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={completedCoursesPercentage} 
                sx={{ 
                  height: 12, 
                  borderRadius: 2,
                  backgroundColor: alpha(getProgressColor(completedCoursesPercentage), 0.2),
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getProgressColor(completedCoursesPercentage),
                    borderRadius: 2
                  }
                }} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontWeight: 600, color: COLORS.dark }}>Promedio de puntuación</Typography>
                <Typography sx={{ fontWeight: 700, color: getProgressColor(averageScorePercentage) }}>
                  {averageScorePercentage}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={Number(averageScorePercentage)} 
                sx={{ 
                  height: 12, 
                  borderRadius: 2,
                  backgroundColor: alpha(getProgressColor(averageScorePercentage), 0.2),
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getProgressColor(averageScorePercentage),
                    borderRadius: 2
                  }
                }} 
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs Content */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '2px solid #e0e0e0',
            overflow: 'hidden'
          }}
        >
          {/* Tabs Header */}
          <Box sx={{ borderBottom: '2px solid #e0e0e0', background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  background: `linear-gradient(90deg, ${COLORS.gradient1}, ${COLORS.gradient2})`
                }
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          backgroundColor: tabValue === index ? alpha(COLORS.primary, 0.15) : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {React.cloneElement(tab.icon, {
                          sx: { fontSize: 20, color: tabValue === index ? COLORS.primary : '#9e9e9e' }
                        })}
                      </Box>
                      <Typography sx={{ fontWeight: tabValue === index ? 700 : 500, fontSize: '0.9rem', color: tabValue === index ? COLORS.primary : '#666' }}>
                        {tab.label}
                      </Typography>
                    </Box>
                  }
                  sx={{ textTransform: 'none', minHeight: 64, px: 3 }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ p: 3, background: '#fafafa' }}>
            {/* Tab 0: Progreso de Cursos */}
            {tabValue === 0 && (
              userCourses.length > 0 ? (
                <>
                  <Grid container spacing={3}>
                    {currentCourses.map(course => {
                      const status = getCourseStatus(course);
                      return (
                        <Grid item xs={12} sm={6} md={4} key={course.id}>
                          <Card 
                            elevation={0}
                            sx={{ 
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              borderRadius: 3,
                              border: `2px solid ${alpha(status.color, 0.2)}`,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 24px ${alpha(status.color, 0.15)}`,
                                borderColor: alpha(status.color, 0.4)
                              }
                            }}
                          >
                            <Box sx={{ height: 4, background: `linear-gradient(90deg, ${status.color} 0%, ${alpha(status.color, 0.5)} 100%)` }} />
                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark, mb: 2, lineHeight: 1.3, minHeight: 52 }}>
                                {course.title}
                              </Typography>
                              
                              <Chip
                                label={course.category}
                                size="small"
                                sx={{ alignSelf: 'flex-start', mb: 2, bgcolor: alpha(COLORS.info, 0.15), color: COLORS.info, fontWeight: 600, border: 'none' }}
                              />

                              <Box sx={{ mb: 2, flex: 1 }}>
                                <Typography variant="body2" sx={{ color: '#636e72', mb: 0.5 }}>
                                  📖 Lecciones: {course.completed ? course.total_lessons : (course.completed_lessons || 0)}/{course.total_lessons || 0}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#636e72', mb: 0.5 }}>
                                  📝 Tests: {course.passed_attempts || 0} aprobados de {course.total_attempts || 0}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#636e72' }}>
                                  🎯 Puntuación: {course.test_score !== null ? `${course.test_score}%` : 'No realizado'}
                                </Typography>
                              </Box>

                              <Chip
                                icon={status.icon}
                                label={status.text}
                                sx={{
                                  mb: 2,
                                  bgcolor: alpha(status.color, 0.15),
                                  color: status.color,
                                  fontWeight: 600,
                                  border: 'none',
                                  '& .MuiChip-icon': { color: status.color }
                                }}
                              />
                              
                              <Button 
                                fullWidth
                                variant="contained"
                                disableElevation
                                startIcon={<PlayArrowIcon />}
                                onClick={() => handleCourseClick(course.id)}
                                sx={{
                                  py: 1.2,
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  background: `linear-gradient(135deg, ${status.color} 0%, ${alpha(status.color, 0.8)} 100%)`,
                                  '&:hover': {
                                    background: `linear-gradient(135deg, ${status.color} 0%, ${alpha(status.color, 0.9)} 100%)`
                                  }
                                }}
                              >
                                {course.completed ? 'Volver a realizar' : 'Continuar curso'}
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination 
                      count={Math.ceil(userCourses.length / coursesPerPage)}
                      page={currentPage}
                      onChange={(event, value) => setCurrentPage(value)}
                      size="large"
                      sx={{
                        '& .MuiPaginationItem-root': {
                          fontWeight: 600,
                          borderRadius: 2,
                          '&.Mui-selected': {
                            background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                            color: 'white'
                          }
                        }
                      }}
                    />
                  </Box>
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <SchoolIcon sx={{ fontSize: 64, color: alpha(COLORS.primary, 0.3), mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#636e72' }}>No tienes cursos asignados</Typography>
                </Box>
              )
            )}

            {/* Tab 1: Calendario */}
            {tabValue === 1 && (
              <MyCalendar events={generateCalendarEvents(courseHistory)} deadlines={deadlines} />
            )}

            {/* Tab 2: Certificados */}
            {tabValue === 2 && (
              <Box>
                {/* Filters */}
                <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: '2px solid #e0e0e0' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Buscar certificado..."
                        value={certificateSearchTerm}
                        onChange={(e) => setCertificateSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: COLORS.gradient1 }} />
                            </InputAdornment>
                          )
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <FilterListIcon sx={{ fontSize: 18 }} />
                            Filtrar por curso
                          </Box>
                        </InputLabel>
                        <Select
                          value={selectedCourse}
                          onChange={handleCourseSelect}
                          label="Filtrar por curso xxx"
                          sx={{ borderRadius: 2 }}
                        >
                          <MenuItem value="">Todos los cursos</MenuItem>
                          {availableCourses.map((course) => (
                            <MenuItem key={course} value={course}>{course}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>

                {filteredCertificates.length > 0 ? (
                  <>
                    <Grid container spacing={3}>
                      {currentCertificates.map(cert => (
                        <Grid item xs={12} sm={6} md={4} key={cert.id}>
                          <Card 
                            elevation={0}
                            sx={{ 
                              borderRadius: 3,
                              border: `2px solid ${alpha(COLORS.purple, 0.2)}`,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 24px ${alpha(COLORS.purple, 0.15)}`,
                                borderColor: alpha(COLORS.purple, 0.4)
                              }
                            }}
                          >
                            <Box sx={{ height: 4, background: `linear-gradient(90deg, ${COLORS.purple} 0%, ${alpha(COLORS.purple, 0.5)} 100%)` }} />
                            <CardContent sx={{ p: 3 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: alpha(COLORS.purple, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <EmojiEventsIcon sx={{ color: COLORS.purple, fontSize: 24 }} />
                                </Box>
                                <Typography variant="body2" sx={{ color: '#636e72', fontWeight: 500 }}>
                                  {new Date(cert.completion_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                                </Typography>
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark, mb: 2, minHeight: 52, lineHeight: 1.3 }}>
                                {cert.course_title}
                              </Typography>
                              <Button 
                                fullWidth
                                variant="contained"
                                disableElevation
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownloadCertificate(cert.pdf_path, cert.course_title)}
                                sx={{
                                  py: 1.2,
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  background: `linear-gradient(135deg, ${COLORS.purple} 0%, ${alpha(COLORS.purple, 0.8)} 100%)`
                                }}
                              >
                                Descargar Certificado
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                      <Pagination 
                        count={Math.ceil(filteredCertificates.length / certificatesPerPage)}
                        page={certificatePage}
                        onChange={(event, value) => setCertificatePage(value)}
                        size="large"
                        sx={{
                          '& .MuiPaginationItem-root': {
                            fontWeight: 600,
                            borderRadius: 2,
                            '&.Mui-selected': {
                              background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                              color: 'white'
                            }
                          }
                        }}
                      />
                    </Box>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <EmojiEventsIcon sx={{ fontSize: 64, color: alpha(COLORS.purple, 0.3), mb: 2 }} />
                    <Typography variant="h6" sx={{ color: '#636e72' }}>No se encontraron certificados</Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Tab 3: Historial de Tests */}
            {tabValue === 3 && (
              <TestHistory data={testHistory} />
            )}
          </Box>
        </Paper>
      </Container>

      {/* MODAL PREVIEW CERTIFICADO */}
      <Dialog
        open={!!previewPdf}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh', borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>{previewTitle}</Typography>
          <IconButton onClick={handleClosePreview}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          {previewPdf && (
            <iframe
              src={previewPdf}
              title="Certificado"
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClosePreview} color="inherit">Cerrar</Button>
          <Button onClick={handleActualDownload} variant="contained" startIcon={<DownloadIcon />}
            sx={{ background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)' }}>
            Abrir PDF completo
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;